// Dependencies
import { type AxiosResponse } from 'axios';

// Stores
import store from '~/components/betslip/betslip.store';

import { $http, type IHttpOptions } from '../util/$http';

class CacheService {
	private static instance: CacheService = new CacheService();
	private static DEV_ENV: boolean;

	private updateCache: (key: string, val: any) => void;
	private updateClient: (key: string, val: any) => void;
	private updateServer: (key: string, val: any) => void;

	constructor() {
		this.updateCache = store.getState().updateCache;
		this.updateClient = store.getState().updateClient;
		this.updateServer = store.getState().updateServer;

		if (CacheService.instance) {
			throw new Error(
				"CacheService is a singleton, you can't instantiate this.",
			);
		}

		CacheService.instance = this;
	}

	public static getInstance(DEV_ENV: boolean = false): CacheService {
		CacheService.DEV_ENV = DEV_ENV;

		return CacheService.instance;
	}

	private updateTree(path: Array<string>, value: any): void {
		// remove the `complete` field that comes in json requests which is a legacy thing
		// TODO: ask backend to remove this default behaviour as we may need the complete` key in the future
		if (typeof value.complete !== undefined) {
			delete value.complete;
		}

		const storeToUpdate = path[0];
		const updateMethod =
			storeToUpdate === 'server' ? this.updateServer : this.updateClient;

		Object.keys(value).forEach(key => {
			updateMethod(Array.isArray(value[key]) ? value[key] : { ...value[key] }, [
				...path,
				key,
			]);
		});
	}

	private updateStoreCache(path: string, value: any) {
		const cacheObject = {
			id: path[1],
			data: value,
			timestamp: Date.now() / 1000,
		};
		this.updateCache(path, cacheObject);
	}

	public getData(
		url: string,
		path: Array<string>,
		redisKey: string,
		checkRedis = true,
	): Promise<void> {
		return new Promise((resolve, _reject) => {
			const cachedData = !redisKey ? null : store.getState().cache[redisKey];

			if (cachedData) {
				if (checkRedis) {
					this.checkTimestamp(cachedData)
						.then(isCacheUpToDate => {
							if (isCacheUpToDate) {
								this.updateTree(path, cachedData.data);
								resolve();
							} else {
								resolve(this.fetchNewData(url, path, redisKey));
							}
						})
						.catch(() => {
							resolve(this.fetchNewData(url, path, redisKey));
						});
				} else {
					this.updateTree(path, cachedData.data);
					resolve();
				}
			} else {
				resolve(this.fetchNewData(url, path, redisKey));
			}
		});
	}

	private checkTimestamp(cachedData: any): Promise<any> {
		return new Promise((resolve, reject) => {
			const baseUrl = store.getState().client.config.baseUrl;

			const options: IHttpOptions = {
				method: 'GET',
				url: `${baseUrl}redis/lastUpdated`,
				params: { key: cachedData.id },
			};

			$http(options)
				.then((res: AxiosResponse) => {
					if (res.status === 200) {
						resolve(cachedData.timestamp >= res.data.value);
					} else {
						resolve(false);
					}
				})
				.catch(e => {
					if (CacheService.DEV_ENV) {
						console.log(e);
					}
					reject(e);
				});
		});
	}

	private fetchNewData(
		url: string,
		path: Array<string>,
		redisKey = '',
	): Promise<void> {
		return new Promise((resolve, reject) => {
			this.updateClient('showSpinner', true);

			const options: IHttpOptions = {
				method: 'GET',
				url,
			};

			$http(options)
				.then((res: AxiosResponse) => {
					if (res.status === 200) {
						const response = res.data;
						this.updateTree(path, response.data);
						this.updateStoreCache(redisKey, response.data);
						resolve();
					} else if (res.status === 204) {
						this.updateTree(path, {});
						this.updateStoreCache(redisKey, {});
						resolve();
					} else {
						reject(res);
					}
				})
				.catch(e => {
					this.updateClient('ajaxError', true);
					reject(e);
				})
				.finally(() => {
					this.updateClient('showMenu', false);
					this.updateClient('showAllSports', false);
					this.updateClient('showSpinner', false);
					this.updateClient('showAllSportsBottom', false);
					this.updateClient('navDrawerOpen', false);
					this.updateClient('betlsipOpen', false);
				});
		});
	}
}

export default CacheService;
